import React, { useState } from 'react'
import { useStationOperationsExport } from '../hooks/useStationOperationsExport'
import { collectStationOperationsRows } from 'BoostXpressAdmin/Utils/stationOperationsExport'
import { StationLookupOption } from 'BoostXpressAdmin/Services/stationLookupBXService'
import StationOperationsStationFilter from '../components/StationOperationsStationFilter'
import filterIcon from 'BoostXpressAdmin/Assets/Icon/Stations/filter.svg'
import exportIcon from 'BoostXpressAdmin/Assets/Icon/Stations/export.svg'
import { reconciliationBXService } from 'BoostXpressAdmin/Services/reconciliationBXService'
import {
  ReconciliationItem,
  ReconciliationStatus,
} from 'BoostXpressAdmin/Model/reconciliationXModel'
import { useStationOperationsPage } from '../hooks/useStationOperationsList'
import ListPagination from 'Components/ListPagination/ListPagination'
import { useListPagination } from 'Hooks/useListPagination'
import {
  formatStationLitres,
  stationOperationsError,
} from 'BoostXpressAdmin/Utils/stationOperations'
import './Reconciliation.scss'

const Reconciliation: React.FC = () => {
  const { exportCsv: downloadCsv, isExporting, exportError } = useStationOperationsExport()
  const [selectedStation, setSelectedStation] = useState<StationLookupOption | null>(null)
  const [productFilter, setProductFilter] = useState<'all' | 'PMS' | 'AGO'>('all')
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const { page, setPage, limit, changePageSize } = useListPagination()

  const filters: Omit<Parameters<typeof reconciliationBXService.list>[0], 'page' | 'limit'> = {
    stationId: selectedStation?.id,
    product: productFilter === 'all' ? undefined : productFilter,
  }

  const {
    items: items,
    total,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useStationOperationsPage<ReconciliationItem, { stationId?: string; product?: 'PMS' | 'AGO' }>(
    'reconciliation',
    reconciliationBXService.list,
    filters,
    page,
    limit,
  )
  const exportCsv = () =>
    downloadCsv(
      () => collectStationOperationsRows('reconciliation', reconciliationBXService.list, filters),
      (item) => ({
        Station: item.station,
        Product: item.product,
        'Report Date': item.reportDate,
        'Pump Sales': `${item.pumpSales.toLocaleString()} L`,
        'Tank Movement': `${item.tankMovement.toLocaleString()} L`,
        Variance: formatStationLitres(item.variance),
        Status: item.status,
      }),
      'reconciliation',
    )
  const renderVariance = (val: number | null) => {
    if (val === null) return <span>Not available</span>
    if (val > 0) {
      return (
        <span className='bx-rec-val--positive'>
          {val.toLocaleString('en-NG', { maximumFractionDigits: 2 })} L
        </span>
      )
    }
    if (val < 0) {
      return (
        <span className='bx-rec-val--negative'>
          {val.toLocaleString('en-NG', { maximumFractionDigits: 2 })} L
        </span>
      )
    }
    return <span className='bx-rec-val--neutral'>0.0 L</span>
  }

  const renderStatusBadge = (status: ReconciliationStatus) => {
    switch (status) {
      case 'Balanced':
        return <span className='bx-rec-pill bx-rec-pill--balanced'>Balanced</span>
      case 'Excess':
        return <span className='bx-rec-pill bx-rec-pill--excess'>Excess</span>
      case 'Deficit':
        return <span className='bx-rec-pill bx-rec-pill--deficit'>Deficit</span>
      default:
        return <span className='bx-rec-pill'>{status}</span>
    }
  }

  return (
    <div className='bx-reconciliation-page'>
      {exportError && (
        <div className='alert alert-danger' role='alert'>
          {exportError}
        </div>
      )}
      {isError && (
        <div className='alert alert-danger' role='alert'>
          {stationOperationsError(error)}{' '}
          <button type='button' onClick={() => refetch()}>
            Try again
          </button>
        </div>
      )}
      {/* Header section matching Figma */}
      <div className='bx-reconciliation-page__header'>
        <h2 className='bx-reconciliation-page__title'>Reconciliation</h2>
        <p className='bx-reconciliation-page__subtitle'>
          Volume variance by product across station reports. Left is negative, right is positive,
          center is balanced.
        </p>
      </div>

      {/* Controls Bar: full-width search and action buttons */}
      <div className='bx-reconciliation-page__controls'>
        <StationOperationsStationFilter
          value={selectedStation}
          onChange={(station) => {
            setSelectedStation(station)
            setPage(1)
          }}
        />

        <div className='bx-reconciliation-page__actions'>
          <div className='bx-rec-filter-dropdown-wrapper'>
            <button
              type='button'
              className={`bx-btn-outline ${isFilterOpen ? 'active' : ''}`}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <img src={filterIcon} alt='' />
              <span>Filters</span>
            </button>

            {isFilterOpen && (
              <div className='bx-rec-filter-menu'>
                <div className='bx-rec-filter-menu__title'>Filter by Product</div>
                <button
                  type='button'
                  className={`bx-rec-filter-menu__item ${productFilter === 'all' ? 'selected' : ''}`}
                  onClick={() => {
                    setProductFilter('all')
                    setPage(1)
                    setIsFilterOpen(false)
                  }}
                >
                  All Products
                </button>
                <button
                  type='button'
                  className={`bx-rec-filter-menu__item ${productFilter === 'PMS' ? 'selected' : ''}`}
                  onClick={() => {
                    setProductFilter('PMS')
                    setPage(1)
                    setIsFilterOpen(false)
                  }}
                >
                  PMS
                </button>
                <button
                  type='button'
                  className={`bx-rec-filter-menu__item ${productFilter === 'AGO' ? 'selected' : ''}`}
                  onClick={() => {
                    setProductFilter('AGO')
                    setPage(1)
                    setIsFilterOpen(false)
                  }}
                >
                  AGO
                </button>
              </div>
            )}
          </div>

          <button
            type='button'
            className='bx-btn-outline'
            onClick={exportCsv}
            disabled={isFetching || isExporting || isError || !total}
          >
            <img src={exportIcon} alt='' />
            <span>{isExporting ? 'Exporting...' : 'Export'}</span>
          </button>
        </div>
      </div>

      {/* Reconciliation Table */}
      <div className='bx-reconciliation-page__table-wrapper'>
        <table className='bx-reconciliation-table'>
          <thead>
            <tr>
              <th>Station</th>
              <th>Product</th>
              <th>Report date</th>
              <th>Pump sales</th>
              <th>Tank movement</th>
              <th>Variance</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className='bx-rec-empty'>
                  Loading reconciliation data...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={7} className='bx-rec-empty'>
                  No reconciliation records found.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td className='bx-rec-col-station'>{item.station}</td>
                  <td>{item.product}</td>
                  <td className='bx-rec-col-date'>{item.reportDate}</td>
                  <td className='bx-rec-col-pumps'>{item.pumpSales.toLocaleString()} L</td>
                  <td className='bx-rec-col-tank'>{item.tankMovement.toLocaleString()} L</td>
                  <td className='bx-rec-col-variance'>{renderVariance(item.variance)}</td>
                  <td className='bx-rec-col-status'>{renderStatusBadge(item.status)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ListPagination
        page={page}
        limit={limit}
        total={total}
        disabled={isFetching}
        onPageChange={setPage}
        onPageSizeChange={changePageSize}
      />
    </div>
  )
}

export default Reconciliation
